import { action, makeObservable, observable, runInAction } from "mobx";
import PlacesApi, { PlacesApi as IPlacesApi } from "../../../../../../services/api/v2/places/Places.api";

import { OrganisationsApi as IOrganisationsApi } from "../../../../../../services/api/v2/organisations/Organisations.api";
import I18n from "../../../../../../core/localization/I18n";
import { AppGridToolbarType } from "../../../../../../core/grids/builder/directorBuilder/types/AppGrid_types";
import { EntityTypes, GridTypes } from "../../../../../../enums";
import ProgressIndicatorModel, {
  IProgressIndicatorModel
} from "../../../../../../components/widgets/ProgressIndicator/ProgressIndicator_model";
import ToasterService, { IToasterService } from "../../../../../../core/toaster/ToasterService";
import { TOASTER_TOAST_TIME } from "../../../../../../core/toaster/Toaster_model";
import { ColDef, GridApi, IDetailCellRendererParams, IRowNode } from "ag-grid-community";
import { GetPlacesGridActions } from "./PlacesGroupsGrid_actions";
import { TextWrapperAddon } from "../../../../../../core/grids/builder/directorBuilder/grids/base/addons/TextWrapAddon";
import { FilterStoreAddon } from "../../../../../../core/grids/builder/directorBuilder/grids/base/addons/FilterAddon/FilterStoreAddon";
import { ColumnStateAddon } from "../../../../../../core/grids/builder/directorBuilder/grids/base/addons/ColumnStateAddon";
import { IGridUiAction } from "../../../../../../core/grids/builder/directorBuilder/grids/base/AppGridToolbarActions_view";
import { AppGridState } from "../../../../../../core/grids/builder/directorBuilder/grids/base/AppGrid_view";
import { SHOW_PLACES_GROUP_DELETE_CONFIRM_MODAL } from "./PlacesGroupsGrid_modals";
import PlacesGroupsApi, {
  PlacesGroupsApi as IPlacesGroupsApi
} from "../../../../../../services/api/v2/places/PlacesGroups.api";
import { PlacesGroupsGridColumnBuilder } from "./PlacesGroupsGrid_columns";
import {
  CommonColDefFieldNamesEnum,
  PlacesColDefFieldNamesEnum
} from "../../../../../../core/grids/builder/enums/AgGridColDefFieldNameEnum";
import { SingleFormModel } from "../../../../../../pages/change/forms/singleFormModel/SingleForm_model";
import { getEntityNameMicroFormFields } from "../../../../../../pages/change/forms/microForm/getEntityNameMicroFormFields";

export class PlacesModel {
  @observable.ref organisation: FP.Entities.IOrganisation;
  organisationProvider: IOrganisationsApi;
  @observable isLoading: boolean = true;
  placesGroupsProvider: IPlacesGroupsApi;
  placesProvider: IPlacesApi;
  @observable.ref places: any[] = [];
  @observable selectedItems: number[] = [];
  @observable isFilterChanged: boolean;
  @observable isColumnStateChanged: boolean;
  microPlaceGroupForm: SingleFormModel;
  organisationId: number;
  projectId: number;
  placeGroupId: number;
  httpProgress: IProgressIndicatorModel;
  toasterService: IToasterService;
  gridApi: GridApi;
  placesGroupsGridColumnBuilder: PlacesGroupsGridColumnBuilder;
  detailGridColumns: ColDef<any, any>[];
  textWrapAddon: TextWrapperAddon;
  filterStoreAddon: FilterStoreAddon;
  columnStateAddon: ColumnStateAddon;
  nestedColumnDefs: FP.Entities.IColumnDef[] = [];
  gridToolbarType: AppGridToolbarType;
  gridType: EntityTypes | GridTypes;
  detailCellRendererParams: IDetailCellRendererParams<any, any>;
  gridColumns: ColDef<any, any>[];
  urlSearchParams: URLSearchParams;
  onFieldUpdate: () => Promise<void>;
  refreshDataHandler?: () => Promise<void>;

  columnDefs: FP.Entities.IColumnDef[] = [];
  @observable.ref gridActions: IGridUiAction[] = [];

  // private typeMap: Record<string, Extract<keyof PlacesModel, string>> = {
  //   locality: "city",
  //   country: "country",
  //   route: "street",
  //   street_number: "streetNumber"
  // };
  modalService: any;

  /**
   *
   */
  constructor(
    organisationId: number,
    projectId: number,
    placeGroupId: number,
    columnDefs: FP.Entities.IColumnDef[],
    gridToolbarType: AppGridToolbarType,
    urlSearchParams: URLSearchParams,
    preSelectedItemIds: number[],
    gridType: EntityTypes | GridTypes,
    onFieldUpdate: () => Promise<void>,
    refreshDataHandler?: () => Promise<void>
  ) {
    makeObservable(this);
    // this.city = "";
    // this.country = "";
    // this.street = "";
    // this.streetNumber = "";
    this.onFieldUpdate = onFieldUpdate;
    this.refreshDataHandler = refreshDataHandler;
    this.placesGroupsProvider = PlacesGroupsApi;
    this.placesProvider = PlacesApi;
    this.organisationId = organisationId;
    this.projectId = projectId;
    this.placeGroupId = placeGroupId;
    this.httpProgress = ProgressIndicatorModel;
    this.columnDefs = columnDefs || [];
    this.gridToolbarType = gridToolbarType;
    this.gridType = gridType;
    this.selectedItems = preSelectedItemIds;
    this.setMicroAddForm();
    this.urlSearchParams = urlSearchParams;
    this.toasterService = ToasterService;
    this.generateAddons();
  }

  @action
  onMount = async () => {
    this.generateFn();
    this.setIsLoading(false);
  };

  onUnmount = () => {};

  @action
  generateFn = () => {
    this.generateGridConfig();
    this.generateActions();
  };
  @action
  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  onRowExpanded = () => {};

  generateActions = () => {
    this.setGridActions(GetPlacesGridActions(this));
  };

  generateAddons = () => {
    this.textWrapAddon = new TextWrapperAddon(null, EntityTypes.PLACES);
    this.filterStoreAddon = new FilterStoreAddon({
      projectId: this.projectId,
      gridType: EntityTypes.PLACES,
      filterHasChangedFn: this.setIsFilterChanged,
      urlSearchParams: this.urlSearchParams
    });

    this.columnStateAddon = new ColumnStateAddon({
      projectId: this.projectId,
      gridType: this.gridType ?? EntityTypes.PLACES,
      columnOrderHasChangedFn: this.setIsColumnStateChanged
    });
  };

  generateGridConfig = () => {
    const canEdit = this.gridToolbarType !== "link-modal";
    this.placesGroupsGridColumnBuilder = new PlacesGroupsGridColumnBuilder({
      placesGroupsProvider: this.placesGroupsProvider,
      canEdit: canEdit,
      organisationId: this.organisationId,
      placeGroupId: this.placeGroupId,
      projectId: this.projectId,
      columns: [
        CommonColDefFieldNamesEnum.Selected,
        CommonColDefFieldNamesEnum.Name,
        CommonColDefFieldNamesEnum.CreatedAt,
        CommonColDefFieldNamesEnum.CreatedBy,
        CommonColDefFieldNamesEnum.UpdatedAt,
        CommonColDefFieldNamesEnum.ModifiedBy,
        PlacesColDefFieldNamesEnum.UiActionCol
      ],
      gridToolbarType: this.gridToolbarType,
      onFieldUpdate: this.onFieldUpdate,
      onRowExpanded: this.onRowExpanded
    });

    this.gridColumns = this.placesGroupsGridColumnBuilder.generateColumnDefs();
  };

  deletePlacesFromGroup = async (k, i) => {
    this.httpProgress.showOverlay();
    await this.placesProvider.remove(this.organisationId, i.data.id);
    this.httpProgress.hideOverlay();
  };
  @action
  onFirstDataRendered = (gridApi: GridApi) => {
    const nodesToSelect: IRowNode[] = [];
    gridApi.forEachNode(node => {
      if (!!this.selectedItems?.includes(node.data.id)) {
        nodesToSelect.push(node);
      }
    });
    gridApi.setNodesSelected({ nodes: nodesToSelect, newValue: true });
  };
  @action
  setMicroAddForm = () => {
    this.microPlaceGroupForm = new SingleFormModel();
    this.microPlaceGroupForm.formFields = getEntityNameMicroFormFields(
      this.createMicroPlaceGroup,
      I18n.t("placeholders.myNewName", { entity: I18n.t("entities.placeGroup") })
    );
  };
  createMicroPlaceGroup = async () => {
    if (!this.microPlaceGroupForm.isSaving) {
      let microPlaceGroupFormRes = await this.microPlaceGroupForm.submit();
      this.microPlaceGroupForm.isSaving = true;
      if (!microPlaceGroupFormRes) {
        this.microPlaceGroupForm.isSaving = false;
        return;
      }

      microPlaceGroupFormRes = {
        ...microPlaceGroupFormRes,
        organisationId: this.organisationId
      };
      const res = {
        ...microPlaceGroupFormRes
      };
      this.httpProgress.showOverlay();
      const result = await this.placesGroupsProvider.create(this.organisationId, res as FP.Entities.IPlace);
      await this.refreshDataHandler();
      this.httpProgress.hideOverlay();

      if (!result || result.isError) {
        this.microPlaceGroupForm.isSaving = false;
        return;
      }
      this.microPlaceGroupForm.isSaving = false;

      const placeGroup = result.payload;
      this.microPlaceGroupForm.resetFields();
      this.toasterService
        .showSuccessToast()
        .setContent(<span>{I18n.t("phrases.itemCreatedSuccessfully", { item: I18n.t("entities.placeGroup") })}</span>)
        .startTimer(TOASTER_TOAST_TIME.NORMAL);
      return placeGroup;
    }
  };
  @action
  onGridStateUpdate = (gridState: AppGridState) => {
    this.gridApi = gridState.gridApi;
    this.selectedItems = gridState.selectedItems || [];
  };

  @action
  setIsColumnStateChanged = (isColumnStateChanged: boolean) => {
    this.isColumnStateChanged = isColumnStateChanged;
    this.generateActions();
  };

  @action
  setIsFilterChanged = (isFilterChanged: boolean) => {
    this.isFilterChanged = isFilterChanged;
    this.generateActions();
  };

  @action
  getPlaceGroups = async () => {
    this.isLoading = true;
    const placeGroups = await this.placesGroupsProvider.getPlaceGroups(this.organisationId);
    runInAction(() => {
      this.setPlaces(placeGroups.payload);
      this.isLoading = false;
    });
  };
  @action
  setSelectedNodes = (gridApi: GridApi) => {
    const nodesToSelect: IRowNode[] = [];
    gridApi.forEachNode(node => {
      if (!!this.selectedItems?.includes(node.data.id)) {
        nodesToSelect.push(node);
      }
    });
    gridApi.setNodesSelected({ nodes: nodesToSelect, newValue: true });
  };
  showPlacesConfirmDeleteModal = () => {
    return SHOW_PLACES_GROUP_DELETE_CONFIRM_MODAL(this.selectedItems, this.removeItems);
  };

  @action
  setPlaces = (places: any[]) => {
    this.places = places;
  };

  removeItems = async (itemIds: number[]) => {
    this.httpProgress.showOverlay();
    let res = await this.placesGroupsProvider.deleteRange(this.organisationId, itemIds);
    if (res && !res.isError) {
      await this.refreshDataHandler();
    }
    this.httpProgress.hideOverlay();

    return res.payload;
  };

  @action
  setGridActions = (gridActions: any) => {
    this.gridActions = gridActions;
  };
}
